import React, { useState, useEffect, useContext } from 'react';
import { Box, Button, Fade, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import { MetrcLogo } from '../../utility';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import SearchIcon from '@mui/icons-material/Search';
import { metrcTagsClasses, roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';

export default function _TransferTab(props) {
  const { currDarkThemeClass = '', handleErrorWithFeedback = () => {}, ...otherProps } = props;

  const [syncLoading, setSyncLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [rawData, setRawData] = useState([]);

  const feedbackCX = useContext(FeedbackContext);

  const fetchTransfersData = async () => {
    setLoading(true);
    try {
      let url = `cropplan-transfers/`;
      const response = await apiService.fetchInstance(url);

      if (response?.data) {
        const fetchedData = Array.isArray(response.data) ? response.data : response.data.results || [];
        setRawData(fetchedData);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Failed to fetch transfers.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfersData();
  }, []);

  useEffect(() => {
    // Client-side pagination & filtering since backend API is not paginated
    let filteredData = rawData;

    if (searchTerm) {
      filteredData = filteredData.filter(item => {
        const destName = (item.destination_name || '').toLowerCase();
        const typeName = (item.transfer_type_name || '').toLowerCase();
        const invoice = (item.invoice_number || '').toLowerCase();
        const s = searchTerm.toLowerCase();
        return destName.includes(s) || typeName.includes(s) || invoice.includes(s);
      });
    }

    setTotalCount(filteredData.length);

    const startIdx = page * rowsPerPage;
    const paginated = filteredData.slice(startIdx, startIdx + rowsPerPage);

    setData(formatData(paginated));
  }, [rawData, searchTerm, page, rowsPerPage]);

  const handleSyncToMetrc = async () => {
    setSyncLoading(true);
    let syncToMetrcUrl = `cropplan-transfers/?sync_to_metrc=true`;

    try {
      const response = await apiService.fetchInstance(syncToMetrcUrl);
      if (response.status === 200) {
        fetchTransfersData();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Transfers Synced From Metrc Successfully.',
        });
        setTimeout(() => {
          feedbackCX.setContextValue(false, {});
        }, 5000);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: `Error syncing from Metrc`,
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const formatData = paginatedData => {
    return paginatedData.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      item.metrc_transfer_id || 'N/A',
      item.destination_name || 'N/A',
      item.transfer_type_name || 'N/A',
      item.planned_route || 'N/A',
      item.invoice_number || 'N/A',
      item.est_dept_date_time ? new Date(item.est_dept_date_time).toLocaleString() : 'N/A',
      item.est_arr_date_time ? new Date(item.est_arr_date_time).toLocaleString() : 'N/A',
      item.transporter_name || 'N/A',
    ]);
  };

  const itemKeyTitleMap = [
    { title: 'Transfer ID' },
    { title: 'Destination' },
    { title: 'Transfer Type' },
    { title: 'Route' },
    { title: 'Invoice Number' },
    { title: 'Est. Dept' },
    { title: 'Est. Arr' },
    { title: 'Transporter' },
  ];

  const tableTitles = [{ title: 'Sl. No' }, ...itemKeyTitleMap];

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const displayProps = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Transfers Found',
    entity: 'Transfers',
    tableEntries: data,
    tableTitles,
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
    ...otherProps,
  };

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses?.filterInput || ''} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search Transporter, Destination...',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              className={`${metrcTagsClasses?.filterTitleTypo || ''} ${currDarkThemeClass}`}
              sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
            >
              Transfers
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${tableClasses?.container || ''} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>
      </Box>
    </Fade>
  );
}
